import { db, type LiftXDatabase } from '../db';
import { SCHEMA_VERSION } from '../db';
import { APP_VERSION } from '../../app/appInfo';

/**
 * Minimal JSON export. Phase 0 §11.2.
 *
 * A **versioned envelope**, not a raw IndexedDB dump: readable, portable across
 * schema versions, inspectable, and recoverable by hand if every other
 * mechanism fails. That last property is the point — a backup you cannot read
 * without the application that wrote it is a hostage, not a backup.
 *
 * **The catalogue is included by reference only.** The 1,500 bundled exercises
 * ship with the app; what is yours is which ones you favourited, archived or
 * overrode. Writing 138.8 MB of someone else's AGPL data into every backup
 * would be wasteful and would carry licensing weight for no benefit (§11.1).
 *
 * Phase 3 scope is **export only**. Import/restore, the `.zip` variant carrying
 * user media, and scheduled automatic backups are later phases (§9); this is
 * the half that protects data today.
 */

export const BACKUP_FORMAT = 'liftx.backup';
export const BACKUP_FORMAT_VERSION = 1;

/** What the user actually owns. Catalogue rows appear only as the reference lists. */
export interface BackupPayload {
  /** User-created exercises, in full. */
  customExercises: unknown[];
  /**
   * Catalogue rows the user has touched — by id plus the user-owned flags
   * only, never the catalogue's own content.
   */
  catalogueExerciseState: {
    id: string;
    isFavourite: boolean;
    isArchived: boolean;
    hasManualOverride: boolean;
    notes: string | null;
    tags: string[];
    defaultRestSeconds: number | null;
    defaultIncrementG: number | null;
  }[];
  plans: unknown[];
  planDays: unknown[];
  planDaySchedules: unknown[];
  planBlocks: unknown[];
  planExercises: unknown[];
  planSets: unknown[];
  progressionRules: unknown[];
  sessions: unknown[];
  sessionBlocks: unknown[];
  sessionExercises: unknown[];
  sessionSets: unknown[];
  cardioSegments: unknown[];
  personalRecords: unknown[];
  measurementTypes: unknown[];
  bodyMeasurements: unknown[];
  historyEditAudit: unknown[];
  settings: Record<string, unknown>;
}

export interface BackupEnvelope {
  format: typeof BACKUP_FORMAT;
  formatVersion: number;
  schemaVersion: number;
  appVersion: string;
  createdAt: string;
  counts: Record<string, number>;
  /** SHA-256 of the serialised payload, hex. Detects truncation and tampering. */
  checksum: string;
  payload: BackupPayload;
}

/** SHA-256 over a UTF-8 string, hex-encoded. Web Crypto — no dependency. */
export async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * The checksum covers the payload **only**, not the envelope.
 *
 * Deliberate: `createdAt` and `counts` are metadata about the export, so
 * including them would make the checksum unverifiable by anyone reconstructing
 * the payload independently. Payload-only means "is my data intact", which is
 * the question a backup needs to answer.
 */
export async function checksumForPayload(payload: BackupPayload): Promise<string> {
  return await sha256Hex(JSON.stringify(payload));
}

export async function buildBackup(database: LiftXDatabase = db): Promise<BackupEnvelope> {
  const allExercises = await database.exercises.toArray();

  const customExercises = allExercises.filter((row) => row.source === 'custom');

  // A catalogue row is only worth recording if the user has actually done
  // something to it. Exporting all 1,500 default rows would bloat the file
  // with information the app already has.
  const catalogueExerciseState = allExercises
    .filter(
      (row) =>
        row.source === 'exercisedb' &&
        (row.isFavourite ||
          row.isArchived ||
          row.hasManualOverride ||
          row.notes !== null ||
          row.tags.length > 0 ||
          row.defaultRestSeconds !== null ||
          row.defaultIncrementG !== null),
    )
    .map((row) => ({
      id: row.id,
      isFavourite: row.isFavourite,
      isArchived: row.isArchived,
      hasManualOverride: row.hasManualOverride,
      notes: row.notes,
      tags: row.tags,
      defaultRestSeconds: row.defaultRestSeconds,
      defaultIncrementG: row.defaultIncrementG,
    }));

  const settingRows = await database.settings.toArray();

  const payload: BackupPayload = {
    customExercises,
    catalogueExerciseState,
    plans: await database.plans.toArray(),
    planDays: await database.planDays.toArray(),
    planDaySchedules: await database.planDaySchedules.toArray(),
    planBlocks: await database.planBlocks.toArray(),
    planExercises: await database.planExercises.toArray(),
    planSets: await database.planSets.toArray(),
    progressionRules: await database.progressionRules.toArray(),
    sessions: await database.sessions.toArray(),
    sessionBlocks: await database.sessionBlocks.toArray(),
    sessionExercises: await database.sessionExercises.toArray(),
    sessionSets: await database.sessionSets.toArray(),
    cardioSegments: await database.cardioSegments.toArray(),
    personalRecords: await database.personalRecords.toArray(),
    measurementTypes: await database.measurementTypes.toArray(),
    bodyMeasurements: await database.bodyMeasurements.toArray(),
    historyEditAudit: await database.historyEditAudit.toArray(),
    // `auth.lastVerified` is excluded: it is a device-local verification
    // timestamp, not user data, and restoring it onto another device would be
    // meaningless at best.
    settings: Object.fromEntries(
      settingRows.filter((row) => row.key !== 'auth.lastVerified').map((row) => [row.key, row.value]),
    ),
  };

  return {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    appVersion: APP_VERSION,
    createdAt: new Date().toISOString(),
    counts: Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        Array.isArray(value) ? value.length : Object.keys(value as object).length,
      ]),
    ),
    checksum: await checksumForPayload(payload),
    payload,
  };
}

/** Re-derive the checksum and compare. Used by tests today, by import later. */
export async function verifyBackup(envelope: BackupEnvelope): Promise<boolean> {
  return (await checksumForPayload(envelope.payload)) === envelope.checksum;
}

export function backupFileName(now: Date = new Date()): string {
  return `liftx-backup-${now.toISOString().slice(0, 10)}.json`;
}

/**
 * Hand the file to the user.
 *
 * iOS has no File System Access API, so this is a Blob plus a generated link —
 * which on iOS opens the share sheet, letting a backup go to Files, iCloud
 * Drive, or anywhere else (§11.3).
 */
export async function downloadBackup(database: LiftXDatabase = db): Promise<string> {
  const envelope = await buildBackup(database);
  const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const fileName = backupFileName();

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoked on the next tick rather than immediately: Safari has historically
  // needed the URL to outlive the click handler.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return fileName;
}
